/**
 * Reusable scroll-reveal helpers powered by framer-motion.
 * Mirrors the GSAP/ScrollTrigger reveal pattern used by premium SaaS landing pages
 * (voxr.ai etc.) but without the extra dependency.
 */
import React from 'react';
import { motion } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1]; // ease-out-expo, used by most premium landings

/**
 * Voxr-style character-level reveal. Splits text by characters but preserves
 * word boundaries (no broken words on line wrap). Each char rises from below
 * with opacity 0 → 1, staggered by `charDelay`.
 */
export function SplitText({ text, className = '', baseDelay = 0, charDelay = 0.025, as: Tag = 'span' }) {
  const words = text.split(' ');
  let charIndex = 0;
  return (
    <Tag className={className} aria-label={text}>
      {words.map((word, wi) => (
        <span key={wi} className="inline-block whitespace-nowrap" aria-hidden="true">
          {word.split('').map((ch) => {
            const i = charIndex++;
            return (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: '0.5em' }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65, delay: baseDelay + i * charDelay, ease: EASE }}
                style={{ display: 'inline-block', willChange: 'transform' }}
              >
                {ch}
              </motion.span>
            );
          })}
          {wi < words.length - 1 && <span aria-hidden="true">&nbsp;</span>}
        </span>
      ))}
    </Tag>
  );
}

export function Reveal({ children, delay = 0, y = 32, className = '', as: Component = 'div', once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: '-80px' }}
      transition={{ duration: 0.75, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export const containerVariants = {
  hidden: {},
  visible: ({ stagger = 0.08, delayChildren = 0 } = {}) => ({
    transition: { staggerChildren: stagger, delayChildren },
  }),
};

export const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
};

export function StaggeredGroup({ children, className = '', stagger = 0.08, delay = 0, once = true }) {
  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, margin: '-60px' }}
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
      }}
    >
      {children}
    </motion.div>
  );
}

export function StaggeredItem({ children, className = '' }) {
  return (
    <motion.div className={className} variants={itemVariants}>
      {children}
    </motion.div>
  );
}
